// Copyright TraderEvolution Global LTD. © 2017-2024. All rights reserved.

import { ErrorInformationStorage } from "../../Commons/ErrorInformationStorage.js";
import { Resources } from "../../Commons/properties/Resources.js";
import { Color } from "../../Commons/Graphics.js";
import { LinkedSystem, LinkedSystemAccLinkingValue } from "../misc/LinkedSystem.js";
import { AssetBalanceItem } from "../cache/AssetBalanceItem.js";
import { VerticalApplicationPanelNew } from "../cache/VerticalPanel/VerticalApplicationPanelNew.js";
import { VerticalPanelDataProvider } from "../cache/VerticalPanel/VerticalPanelDataProvider.js";
import { VerticalPanelDataProviderItem } from "../cache/VerticalPanel/VerticalPanelDataProviderItem.js";
import { TerceraAccountLookup } from "../elements/Lookup/TerceraAccountLookup.js";
import { QuickTableRow } from "../elements/QuickTable/QuickTableRow.js";
import { TerceraComboBox } from "../elements/TerceraComboBox.js";
import { TerceraLinkControlConstants } from "../UtilsClasses/TerceraLinkControlConstants.js";
import { TerceraLinkControl } from "../elements/TerceraLinkControl.js";
import { TerceraPanel } from "../elements/TerceraPanel.js";
import { PanelNames } from "../UtilsClasses/FactoryConstants.js";
import { ApplicationPanelNew } from "./ApplicationPanelNew.js";
import { ThemeManager } from "../misc/ThemeManager.js";
import { AccountType } from "../../Utils/Account/AccountType.js";
import { QuickTableComparingType } from "../../Utils/QuickTableMisc/QuickTableComparingType.js";
import { AccountAdditionalInfoDestinationPanel } from "../../Utils/Account/AdditionalInfoItem.js";
import { Account } from "../../Commons/cache/Account.ts";
import { DynProperty } from "../../Commons/DynProperty.js";
import { DataCache } from "../../Commons/DataCache.js";
import { SessionSettings } from "../../Commons/SessionSettings.js";
import { AccountWidgetManager } from "../elements/AccountWidgetManager.ts";


export let AccountDetailsPanel = VerticalApplicationPanelNew.extend(
    {
        Name: 'AccountDetailsPanel',
        topPanelHeight: 25,
        data: function ()
        {
            return {
                showTopPanel: true,
                zIndex: 1300,
                showHeader: false,
                withBorderRadius: false,
                showFooter: false,
                dockablePanel: false,
                resizable: false,
                width: 350,
                height: 365,
                closeBtnVisible: false,
            };
        },
        myAccount: null,
        myAccLookup: null,
        myAccLinkBtn: null,
        NeedCalculateRowCount: false,
        headerLocaleKey: 'panel.AccountDetailsPanel',
        skipOnSave: true
    });

AccountDetailsPanel.prototype.getType = function () { return PanelNames.AccountDetailsPanel };

AccountDetailsPanel.prototype.oninit = function ()
{
    VerticalApplicationPanelNew.prototype.oninit.apply(this);
    this.topPanelHeight = this.showTopPanel() ? 25 : 0;
};

AccountDetailsPanel.prototype.showTopPanel = function ()
{
    return true;    // Всегда показываем, чтобы скролл не наезжал на info tooltip

    var dc = DataCache;
    var accounts = dc.Accounts;
    var accKeys = Object.keys(accounts);
    let acc = accounts[accKeys[0]];
    let assets = acc.assetBalances;
    let assetsKeys = Object.keys(assets);

    return accKeys.length > 1 || assetsKeys.length > 1;
};

AccountDetailsPanel.prototype.oncomplete = function ()
{
    VerticalApplicationPanelNew.prototype.oncomplete.apply(this);


    /// <summary>
    /// Кэшируем динамические строки
    /// </summary>
    //Dictionary<string, VerticalPanelDataProviderItem> accountDinamicProperties = new Dictionary<string, VerticalPanelDataProviderItem>();
    let accountDinamicProperties = new Object();

    this.createActionControls();
    this.quickTableRactive.setShowColumnHeaders(false);
    this.quickTableRactive.quickTable.allowGroupBy = false;
    this.quickTableRactive.quickTable.GroupIndexSorting = true;
    this.quickTableRactive.quickTable.useVerticalSeparatorForResizing = true;
    this.quickTableRactive.quickTable.FormatGroup = this.quickTable_FormatGroup.bind(this);

    if (this.deferredLinkInAccId)
    {
        var self = this;
        self.myAccLookup.on('complete', function ()
        {
            self.accountLink_In(self.deferredLinkInAccId);
            delete self.deferredLinkInAccId;
        });
    }

    if (this.deferredAssetId !== undefined)
    {
        var self = this;
        self.assetComboBox.on('complete', function ()
        {
            self.selectAsset(self.deferredAssetId);
            delete self.deferredAssetId;
        });
    }
    // this.on(Control.Events.LostFocus, this.LostFocus);
}

// AccountDetailsPanel.prototype.LostFocus = function ()
// {
//     this.set({ visible: false });
// }

AccountDetailsPanel.prototype.hide = function ()
{
    this.set({ visible: false });

    if (this.myAccLookup)
        this.myAccLookup.onLostFocus();
}

AccountDetailsPanel.prototype.createActionControls = function ()
{
    var rightError = 6 + 10;

    var topPanel = new TerceraPanel();
    topPanel.set({ name: 'topPanel' });
    this.addControl(topPanel);
    topPanel.setBounds(5, 0, this.get('width') - rightError, 30);

    var accLookup = new TerceraAccountLookup();
    accLookup.set({ top: 5, left: 5, width: 100 });
    topPanel.addControl(accLookup);

    var singleAccount = DataCache.AccountsCount === 1

    if (singleAccount)
        this.set({ showTopPanel: false });

    var accLinkBtn = new TerceraLinkControl();
    accLinkBtn.set({
        left: 325, top: 4,
        linkByAccount: true,
        selectedLinkValue: TerceraLinkControlConstants.ColorConstants.NONE,
        visible: !singleAccount,
        tooltip: "panel.AccountLinkButton.ToolTip"
    })

    var self = this;

    accLinkBtn.on('linkClick', function ()      //// https://tp.traderevolution.com/entity/83015 
    {
        let lS = LinkedSystem,
            accId = self.myAccLookup.get('selectedItem').BstrAccount,
            val = accLinkBtn.get('selectedLinkValue') == TerceraLinkControlConstants.ColorConstants.NONE ? LinkedSystemAccLinkingValue : TerceraLinkControlConstants.ColorConstants.NONE

        lS.accLinkingActive = val == LinkedSystemAccLinkingValue

        lS.accLinkingActive ? lS.accLinkingOn(accId) : lS.accLinkingOff()
    });
    this.addControl(accLinkBtn);    // attach to AccountDetailsPanel for correct combobox position

    var assetComboBox = new TerceraComboBox();
    assetComboBox.set({ top: 5, left: singleAccount ? 5 : 110, width: 100, visible: false });
    topPanel.addControl(assetComboBox);

    //#region TODO. Do it over.

    accLookup.observe('selectedItem', function (account)
    {
        let currentGroupAcc = new Object();
        AccountDetailsPanel.SetSource(self.VerticalPanelDataProvider, self.sett, DataCache, account.BstrAccount, currentGroupAcc);
        self.myAccount = account;
        //self.VerticalPanelDataProvider.Source = account;
        if (!account) return;
        self.PopulateTable();


        var sP = self.get('showTopPanel');
        let assets = Object.keys(account.assetBalances);
        if (!sP)
            self.set({ showTopPanel: assets.length > 1 });

        let items = assets.map(function (x)
        {
            return account.assetBalances[x].Asset;
        })
        assetComboBox.set({
            visible: items.length > 1,
            items: self.toComboBoxItems(items)
        });
        // if (DataCache.isAllowedForMyUser(RulesSet.FUNCTION_EXT_RISK) && items.length > 1)
        //     self.setAssetItems(items, account)
    });

    assetComboBox.observe('selectedItem', function (selectedItem)
    {
        if (!selectedItem || !selectedItem.value) return;

        var account = accLookup.get('selectedItem');
        var asset = assetComboBox.count() > 1 ? account.assetBalances[selectedItem.text] : null;
        if (asset)
        {
            self.VerticalPanelDataProvider.source[0].AssetBalance = asset
            self.PopulateTable();
        }
    });
    accLookup.onChangeValue.Subscribe(function (acc)
    {
        let sameAccs = Account.IsEqualAccount(acc, this.myAccount)
        this.myAccount = acc;
        this.accountLink_Out(false, acc, sameAccs);
        AccountWidgetManager.setAccount(acc);
    }, this);

    accLinkBtn.observe('selectedLinkValue', function (newVal, oldVal, key)
    {
        self.accountLink_Out(true);
    });

    accLookup.showMAM = true;
    this.myAccLookup = accLookup;
    this.assetComboBox = assetComboBox;
    this.myAccLinkBtn = accLinkBtn;
    //#endregion
};

AccountDetailsPanel.prototype.setAssetItems = function (items, account, assetProp)
{
    if (assetProp)
        if (account.assetBalances[assetProp.text] && Object.keys(account.assetBalances[assetProp.text].AssetAdditionalInfo).length !== 0)
        {
            this.selectAsset(account.assetBalances[assetProp.text].Asset.Id)
            return true;
        }


    for (let i = 0; i < items.length; i++)
    {
        if (Object.keys(account.assetBalances[items[i].text].AssetAdditionalInfo).length !== 0)
        {
            this.selectAsset(account.assetBalances[items[i].text].Asset.Id)
            return true;
        }
    }
    return false;
}

AccountDetailsPanel.prototype.toComboBoxItems = function (assetIds)
{
    var arr = [];
    var assets = DataCache.assetCollection;
    for (var i = 0; i < assetIds.length; i++)
    {
        var asset = assetIds[i];
        arr.push({ text: asset.Name, value: asset });
    }
    var funcSort = function (a, b)
    {
        if (a.text.toLowerCase() > b.text.toLowerCase())
            return 1;
        else
            return -1;
    }
    arr.sort(funcSort);

    return arr;
};

AccountDetailsPanel.prototype.accountLink_Out = function (newSubscriber, account, sameAccs)
{
    if (!account)
    {
        if (!this.myAccount)
            return;
        account = this.myAccount;
    }
    if (!this.myAccLinkBtn)
        return

    if (LinkedSystem.accLinkingActive)
        LinkedSystem.setAccount(LinkedSystemAccLinkingValue, account.BstrAccount, newSubscriber);

    if (!sameAccs)
        SessionSettings.save()
};

AccountDetailsPanel.prototype.accountLink_In = function (accId)
{
    if (this.myAccLookup)
        this.myAccLookup.setSelectValue(DataCache.Accounts[accId]);
    else
        this.deferredLinkInAccId = accId;
};

AccountDetailsPanel.prototype.selectAsset = function (assetId)
{
    if (!this.assetComboBox)
    {
        this.deferredAssetId = assetId;
        return;
    }

    var items = this.assetComboBox.get('items');
    var len = items ? items.length : 0;
    for (var i = 0; i < len; i++)
    {
        var item = items[i];
        if (item.value.Id === assetId)
        {
            this.assetComboBox.set('selectedItem', item);
            return;
        }
    }
};

AccountDetailsPanel.prototype.Properties = function ()
{
    var properties = ApplicationPanelNew.prototype.Properties.call(this);
    properties.push(new DynProperty("AccountId", this.myAccLookup.get('selectedItem').AcctNumber, DynProperty.STRING, DynProperty.HIDDEN_GROUP));

    return properties;
};

AccountDetailsPanel.prototype.callBack = function (newProperties)
{
    ApplicationPanelNew.prototype.callBack.call(this, newProperties);
    var AccountId = DynProperty.getPropValue(newProperties, "AccountId");
    if (AccountId)
        this.accountLink_In(AccountId);
};

AccountDetailsPanel.prototype.repopulate = function ()
{
    ApplicationPanelNew.prototype.repopulate.apply(this);
    var singleAccount = DataCache.AccountsCount === 1;
    if (this.myAccLookup)
    {
        this.myAccLookup.repopulate();  // accounts list after user change bug fix
        this.myAccLookup.set({ top: 5, left: 5, width: 100, visible: !singleAccount });
    }

    if (this.myAccLinkBtn)
        this.myAccLinkBtn.set({ visible: !singleAccount });

    if (this.assetComboBox)
    {
        let acc = this.myAccount;
        let visible = false;
        if (acc)
            visible = Object.keys(acc.assetBalances).length > 1;
        this.assetComboBox.set({ top: 5, left: singleAccount ? 5 : 110, width: 100, visible: visible });
    }
    this.InitializeDataProvider();


    if (this.myAccLookup)
    {
        let item = null;
        if (this.assetComboBox);
        item = this.assetComboBox.get('selectedItem');
        this.myAccLookup.set({ selectedItem: this.myAccLookup.get('selectedItem') });
        if (item)
            this.assetComboBox.set({ selectedItem: item });
    }
};

/// <summary>
/// вызов из конструктора баз. класса
/// </summary>
AccountDetailsPanel.prototype.InitializeDataProvider = function ()
{
    this.VerticalPanelDataProvider = new VerticalPanelDataProvider(this.sett);

    this.VerticalPanelDataProvider.items = AccountDetailsPanel.getDataItems(this.VerticalPanelDataProvider, this.sett);

    //
    this.VerticalPanelDataProvider.GetVerticalPanelValueHandler = AccountDetailsPanel.GetDataValue.bind(this);
    this.VerticalPanelDataProvider.GetItemsIDHandler = AccountDetailsPanel.GetItemsID.bind(this);
    this.VerticalPanelDataProvider.DisposeHandler = AccountDetailsPanel.DisposeSource.bind(this);
    //
    this.VerticalPanelDataProvider.GetDynamicItemsHandler = this.GetDynamicItems.bind(this);
}

AccountDetailsPanel.prototype.quickTable_FormatGroup = function (groupValue)
{
    return Resources.getResource(groupValue, this.sett.Locale);
}

AccountDetailsPanel.prototype.localize = function ()
{
    VerticalApplicationPanelNew.prototype.localize.call(this)

    if (this.VerticalPanelDataProvider)
        this.VerticalPanelDataProvider.Localize()
}

AccountDetailsPanel.getDataItems = function (VerticalPanelDataProvider, sett)
{
    //#22483 Admin и видимость полей
    // Data items
    let dataItems = new Array();

    let assetBalanceItem = new AssetBalanceItem(null, sett);
    let columnCount = assetBalanceItem.ColumnCount();
    for (let i = 0; i < columnCount; i++)
    {
        // problem: на Reinitialize сбрасывалась настроенная видимость полей, и цвета. а регенерация нужна т.к. админ/не адмир разные поля можно
        let visibilityRestore = null;//VerticalPanelDataProvider.items === null || VerticalPanelDataProvider.items.length === 0 ? null : VerticalPanelDataProvider.items.Find(m => m.Id === i.toString());

        let columnParams = assetBalanceItem.GetColumnParams(i);
        let group = AccountDetailsPanel.GetGroupForKey(columnParams.HeaderKey, sett);
        if (VerticalPanelDataProvider.Source instanceof Array && VerticalPanelDataProvider.Source.length > 1 && group === "panel.accountDetails.Groups.4.RiskManagement" || group === "panel.accountDetails.Groups.6.Info")
            continue;

        let newItem = new VerticalPanelDataProviderItem();
        newItem.Id = i.toString();
        newItem.Hidden = columnParams.Hidden;
        newItem.visible = !columnParams.Hidden && (AccountDetailsPanel.TableItemVisible(i) || (visibilityRestore === null || visibilityRestore.visible));
        newItem.LocalizationKey = columnParams.HeaderKey;
        newItem.tooltipKey = Resources.GetToolTipKey(columnParams.HeaderKey);
        newItem.Group = group;
        newItem.SortIndex = AccountDetailsPanel.GetSortIndexByIndex(i);

        newItem.ValueBackColor = visibilityRestore === null ? Color.Empty : visibilityRestore.ValueBackColor;
        newItem.ValueForeColor = visibilityRestore === null ? Color.Empty : visibilityRestore.ValueForeColor;
        newItem.HeaderBackColor = visibilityRestore === null ? Color.Empty : visibilityRestore.HeaderBackColor;
        newItem.HeaderForeColor = visibilityRestore === null ? Color.Empty : visibilityRestore.HeaderForeColor;
        dataItems.push(newItem);
    }
    assetBalanceItem.Dispose();
    assetBalanceItem = null;

    dataItems.sort(function (a, b)
    {
        return a.SortIndex - b.SortIndex
    })

    return dataItems
}

AccountDetailsPanel.GetGroupForKey = function (headerKey)
{
    switch (headerKey)
    {
        case "panel.accounts.Balance":
        case "panel.accounts.CurBalance":
        case "panel.accounts.BalancePlusAllRisks":
        case "panel.accounts.AccountValue":
        case "panel.accounts.CreditValue":
        case "panel.accounts.AvailableMargin":
        case "panel.accounts.LockedBalance":
        case "panel.accounts.CashBalance":
        case "panel.accounts.WithdrawalAvailable":
        case "panel.accounts.InterestRate":
        case "panel.accounts.Estimate value":
        case "panel.accounts.UnsettledCash":
        case "panel.accounts.IncomingFunds":
        case "panel.accounts.StocksValue":
        case "panel.accounts.OptionValue":
        case "panel.accounts.StocksLiquidity":
        case "panel.accounts.Collateral":
        case "panel.accounts.BuyingPower":
        case "panel.accounts.GivenLeverage":
        case "panel.accounts.OpenBalance":
            return "panel.accountDetails.Groups.1.General";


        case "panel.accounts.FundsUsed":
        case "panel.accounts.FundsUsedPercent":
        case "panel.accounts.MarginUsedNew":
        case "panel.accounts.stopOutPercent":
        case "panel.accounts.CurMargin":
        case "panel.accounts.MaintMargin":
        case "panel.accounts.Leverage":
        case "panel.accounts.MarginAvailableReal":
        case "panel.accounts.marginWarnPercent":
        case "panel.accounts.MarginWarning":
        case "panel.accounts.MarginRequirement":
        case "panel.accounts.MinMargin":
        case "panel.accounts.BlockedByOrders":
        case "panel.accounts.MarginStopOut":
        case "panel.accounts.StocksOrdersReq":
        case "panel.accounts.MarginDeficiency":
        case "panel.accounts.MarginSurplus":
        case "panel.accounts.BlockedForStocks":
        case "panel.accounts.MarginForOthers":
        case "panel.accounts.BlockedForStocksNew":
        case "panel.accounts.OptionPremiumReq":
        case "panel.accounts.WaivedMargin":
        case "panel.accounts.WarningMarginReq":
        case "panel.accounts.WarningMarginReqPercent":
        case "panel.accounts.MarginBeforeWarning":
        case "panel.accounts.InitialMarginWithoutWaived":
        case "panel.accounts.StopOut":
            return "panel.accountDetails.Groups.2.Margin";

        case "panel.accounts.CurrentFundCapital":
        case "panel.accounts.FundCapitalGain":
        case "panel.accounts.Profit":
        case "panel.accounts.ProfitNet":
        case "panel.accounts.Positions":
        case "panel.accounts.Orders":
        //Unsettled cash -  not found
        case "panel.accounts.BlockedByPamm":
            return "panel.accountDetails.Groups.3.AccountActivity";

        case "panel.accounts.GrossPNL":
        case "panel.accounts.BeginBalance":
        case "panel.accounts.TodayFees":
        case "panel.accounts.TodayVolume":
        case "panel.accounts.TodayTrades":
        case "panel.accounts.TodayTurnover":
        case "panel.accounts.TodayRebates":
        case "panel.accounts.TodaySwaps":
            return "panel.accountDetails.Groups.4.TodayResults";

        case "panel.accounts.CurrentStatus":
        case "panel.accounts.AllowTradingPrePost":
        case "panel.accounts.MaxDailyLossLimit":
        //case "panel.accounts.MaxDayVolume":
        case "panel.accounts.MaxOrdersPerDay":
        case "panel.accounts.MaxPositions":
        case "panel.accounts.MaxPendingOrders":
        case "panel.accounts.MaxPendingOrdersValue":
        case "panel.accounts.MaxOrderCapital":
        case "panel.accounts.MaximumLot":
        case "panel.accounts.MaxPositionQtyPerSymbol":
        case "panel.accounts.AllowOvernightTrading":
        case "panel.accounts.TrailingDrawdownLevel":
        case "panel.accounts.TotalMaxPositionsQty":
        case "panel.accounts.MaxWeekLostLimit":
        case "panel.accounts.CurrentDailyLoss":
        case "panel.accounts.CurrentWeeklyLoss":
        case "panel.accounts.UnrealizedLoss":
        case "panel.accounts.UnrealizedLossLimit":
        case "panel.accounts.MaxDailyProfit":
        case "panel.accounts.VolumeLimitForALL":
        case "panel.accounts.VolumeLimitForFOREX":
        case "panel.accounts.VolumeLimitForEQUITIES":
        case "panel.accounts.VolumeLimitForFUTURES":
        case "panel.accounts.VolumeLimitForOPTIONS":
        case "panel.accounts.VolumeLimitForEQUITIES_CFD":
        case "panel.accounts.VolumeLimitForFORWARD":
        case "panel.accounts.VolumeLimitForCFD_FUTURES":
        case "panel.accounts.VolumeLimitForINDICIES":
        case "panel.accounts.VolumeLimitForCRYPTO":
        case "panel.accounts.VolumeLimitForSPREADBET":
        case "panel.accounts.VolumeLimitForBOND":
        case "panel.accounts.VolumeLimitForETF":
        case "panel.accounts.VolumeLimitForTBILL":
        case "panel.accounts.VolumeLimitForSPOT":
        case "panel.accounts.VolumeLimitForCORPORATE":
        case "panel.accounts.MaxOrderAmount":
        case "panel.accounts.DayTraderPatternProtection":
        case "panel.accounts.AvailableDayTrades":
        case "panel.accounts.MaxRelativeDrawDownLevel":
        case "panel.accounts.LossLimitPerTrade":
            return "panel.accountDetails.Groups.5.RiskManagement";

        case "panel.accounts.Commissions":
        case "panel.accounts.Swap":
        case "panel.accounts.Spreads":
        case "panel.accounts.Murkups":
        case "panel.accounts.MarketDataCharging":
        case "panel.accounts.MarginAndRisks":
        case "panel.accounts.InsertOnBalance":
            return "panel.accountDetails.Groups.55.Plans";

        default:
            return "panel.accountDetails.Groups.6.Info";
    }
}
AccountDetailsPanel.TableItemVisible = function (key)
{
    //switch default visibility by id
    try
    {
        switch (key)
        {
            // 1. General
            case 2:
            case 6:
            case 15:
            case 20:
            case 24:
            case 35:
            case 42:
            case 44:
            case 45:
            case 46:
            case 58:
            // 2. Account activity
            case 4:
            case 5:
            case 10:
            case 11:
            case 12:
            case 13:
            // 3. Today results
            case 29:
            case 30:
            case 31:
            case 32:
            case 71:
            // 4. Risk management  // по умолч. всё включено
            case 47:
            case 48:
            case 49:
            case 50:
            case 51:
            case 52:
            case 53:
            case 54:
            case 55:
            case 56:
            case 57:
            case 62:
            case 85:
            // 5. Info
            case 0:
            case 1:
            case 7:
            case 63:
                return true;
            default:
                return false;
        }
    }
    catch (ex)
    {
        ErrorInformationStorage.GetException(ex);
        return false;
    }
}

AccountDetailsPanel.KEY_GENERAL_GROUP = 1;
AccountDetailsPanel.KEY_MARGIN_GROUP = 200;
AccountDetailsPanel.KEY_ACCOUNTACTIVITY_GROUP = 300;
AccountDetailsPanel.KEY_TODAYRESULTS_GROUP = 400;
AccountDetailsPanel.KEY_RISKMANAGEMENT_GROUP = 500;
AccountDetailsPanel.KEY_PLANS_GROUP = 600;
AccountDetailsPanel.KEY_INFO_GROUP = 700;
AccountDetailsPanel.GetSortIndexByIndex = function (columnIndex)
{
    switch (columnIndex)
    {


        //panel.accountDetails.Groups.1.General
        case 2:// panel.accounts.Balance    
            return AccountDetailsPanel.KEY_GENERAL_GROUP + 5;
        case 6: //panel.accounts.CurBalance 
            return AccountDetailsPanel.KEY_GENERAL_GROUP + 10;
        case 43:// panel.accounts.BalancePlusAllRisks  
            return AccountDetailsPanel.KEY_GENERAL_GROUP + 15;
        case 35:// panel.accounts.AccountValue    
            return AccountDetailsPanel.KEY_GENERAL_GROUP + 20;
        case 61: //panel.accounts.CreditValue   
            return AccountDetailsPanel.KEY_GENERAL_GROUP + 25;
        case 20:// panel.accounts.AvailableMargin                                          
            return AccountDetailsPanel.KEY_GENERAL_GROUP + 30;
        case 3: //panel.accounts.LockedBalance    
            return AccountDetailsPanel.KEY_GENERAL_GROUP + 40;
        case 36:// panel.accounts.CashBalance   
            return AccountDetailsPanel.KEY_GENERAL_GROUP + 45;
        case 84: // panel.accounts.UnsettledCash     
            return AccountDetailsPanel.KEY_GENERAL_GROUP + 46;
        case 117: //panel.accounts.IncomingFunds
            return AccountDetailsPanel.KEY_GENERAL_GROUP + 47;
        case 24:// panel.accounts.WithdrawalAvailable       
            return AccountDetailsPanel.KEY_GENERAL_GROUP + 50;
        case 70:// panel.accounts.InterestRate    
            return AccountDetailsPanel.KEY_GENERAL_GROUP + 55;
        case 66: // panel.accounts.Estimate value      
            return AccountDetailsPanel.KEY_GENERAL_GROUP + 60;
        case 34: // panel.accounts.StocksValue                                                
            return AccountDetailsPanel.KEY_GENERAL_GROUP + 70;
        case 92: // panel.accounts.OptionValue
            return AccountDetailsPanel.KEY_GENERAL_GROUP + 75;
        case 93: // panel.accounts.StocksLiquidity
            return AccountDetailsPanel.KEY_GENERAL_GROUP + 80;
        case 103: //panel.accounts.Collateral
            return AccountDetailsPanel.KEY_GENERAL_GROUP + 85;

        //panel.accountDetails.Groups.2.Margin

        case 59: //  panel.accounts.FundsUsed 
            return AccountDetailsPanel.KEY_MARGIN_GROUP + 5;
        case 60: //  panel.accounts.FundsUsedPercent    
            return AccountDetailsPanel.KEY_MARGIN_GROUP + 10;
        case 42: //  panel.accounts.MarginUsedNew  
            return AccountDetailsPanel.KEY_MARGIN_GROUP + 15;
        case 14: //  panel.accounts.CurMargin     
            return AccountDetailsPanel.KEY_MARGIN_GROUP + 20;
        case 108://  panel.accounts.InitialMarginWithoutWaived
            return AccountDetailsPanel.KEY_MARGIN_GROUP + 21;
        case 15: //  panel.accounts.MaintMargin       
            return AccountDetailsPanel.KEY_MARGIN_GROUP + 25;
        case 16: //  panel.accounts.Leverage       
            return AccountDetailsPanel.KEY_MARGIN_GROUP + 30;
        case 58: //  panel.accounts.MarginAvailableReal  
            return AccountDetailsPanel.KEY_MARGIN_GROUP + 35;
        case 17: //  panel.accounts.marginWarnPercent   
            return AccountDetailsPanel.KEY_MARGIN_GROUP + 40;
        case 21: //  panel.accounts.MarginWarning   
            return AccountDetailsPanel.KEY_MARGIN_GROUP + 45;
        case 23: //  panel.accounts.MarginStopOut      
            return AccountDetailsPanel.KEY_MARGIN_GROUP + 50;
        case 44: //  panel.accounts.MarginRequirement   
            return AccountDetailsPanel.KEY_MARGIN_GROUP + 55;
        case 12: //  panel.accounts.MinMargin       
            return AccountDetailsPanel.KEY_MARGIN_GROUP + 60;
        case 13: //  panel.accounts.BlockedByOrders   
            return AccountDetailsPanel.KEY_MARGIN_GROUP + 65;
        case 45: //  panel.accounts.MarginDeficiency   
            return AccountDetailsPanel.KEY_MARGIN_GROUP + 70;
        case 46: //  panel.accounts.MarginSurplus       
            return AccountDetailsPanel.KEY_MARGIN_GROUP + 75;
        case 72: //  panel.accounts.BlockedForStocks       
            return AccountDetailsPanel.KEY_MARGIN_GROUP + 80;
        case 73: //  panel.accounts.MarginForOthers       
            return AccountDetailsPanel.KEY_MARGIN_GROUP + 85;
        case 74: //  panel.accounts.MarginForOthers       
            return AccountDetailsPanel.KEY_MARGIN_GROUP + 90;
        case 91: //panel.accounts.StocksOrdersReq
            return AccountDetailsPanel.KEY_MARGIN_GROUP + 95;
        case 19: //panel.accounts.stopOutPercent      
            return AccountDetailsPanel.KEY_MARGIN_GROUP + 100;
        case 102: //panel.accounts.OptionPremiumReq
            return AccountDetailsPanel.KEY_MARGIN_GROUP + 101;
        case 104: //panel.accounts.WaivedMargin
            return AccountDetailsPanel.KEY_MARGIN_GROUP + 110;
        case 105: //panel.accounts.WarningMarginReq      
            return AccountDetailsPanel.KEY_MARGIN_GROUP + 120;
        case 106: //panel.accounts.WarningMarginReqPercent    
            return AccountDetailsPanel.KEY_MARGIN_GROUP + 130;
        case 107: //panel.accounts.MarginBeforeWarning      
            return AccountDetailsPanel.KEY_MARGIN_GROUP + 140;
        case 115: //panel.accounts.StopOut      
            return AccountDetailsPanel.KEY_MARGIN_GROUP + 150;

        //panel.accountDetails.Groups.3.AccountActivity

        case 39: //   panel.accounts.CurrentFundCapital   
            return AccountDetailsPanel.KEY_ACCOUNTACTIVITY_GROUP + 5;
        case 40: //   panel.accounts.FundCapitalGain  
            return AccountDetailsPanel.KEY_ACCOUNTACTIVITY_GROUP + 10;
        case 5: //   panel.accounts.Profit   
            return AccountDetailsPanel.KEY_ACCOUNTACTIVITY_GROUP + 15;
        case 4: //   panel.accounts.ProfitNet                   
            return AccountDetailsPanel.KEY_ACCOUNTACTIVITY_GROUP + 20;
        case 11: //   panel.accounts.Positions  
            return AccountDetailsPanel.KEY_ACCOUNTACTIVITY_GROUP + 25;
        case 10: //   panel.accounts.Orders 
            return AccountDetailsPanel.KEY_ACCOUNTACTIVITY_GROUP + 30;
        case 37: //   panel.accounts.BlockedByPamm                                                               
            return AccountDetailsPanel.KEY_ACCOUNTACTIVITY_GROUP + 35;

        // panel.accountDetails.Groups.4.TodayResults   
        case 29:  //  panel.accounts.GrossPNL  
            return AccountDetailsPanel.KEY_TODAYRESULTS_GROUP + 5;
        case 31:  //  panel.accounts.BeginBalance   
            return AccountDetailsPanel.KEY_TODAYRESULTS_GROUP + 10;
        case 30:  //  panel.accounts.TodayFees                  
            return AccountDetailsPanel.KEY_TODAYRESULTS_GROUP + 15;
        case 32:  //  panel.accounts.TodayVolume  
            return AccountDetailsPanel.KEY_TODAYRESULTS_GROUP + 20;
        case 33:  //  panel.accounts.TodayTrades   
            return AccountDetailsPanel.KEY_TODAYRESULTS_GROUP + 25;
        case 68:  //  panel.accounts.TodayTurnover   
            return AccountDetailsPanel.KEY_TODAYRESULTS_GROUP + 30;
        case 71:  //  panel.accounts.TodayRebates                   
            return AccountDetailsPanel.KEY_TODAYRESULTS_GROUP + 35;



        // panel.accountDetails.Groups.5.RiskManagement   

        case 48: // panel.accounts.CurrentStatus     
            return AccountDetailsPanel.KEY_RISKMANAGEMENT_GROUP + 5;
        case 49: // panel.accounts.AllowTradingPrePost   
            return AccountDetailsPanel.KEY_RISKMANAGEMENT_GROUP + 10;
        case 50: // panel.accounts.MaxDailyLossLimit      
            return AccountDetailsPanel.KEY_RISKMANAGEMENT_GROUP + 15;
        case 89: // panel.accounts.MaxDailyLossLimit      
            return AccountDetailsPanel.KEY_RISKMANAGEMENT_GROUP + 16;
        case 88: // panel.accounts.MaxWeekLostLimit
            return AccountDetailsPanel.KEY_RISKMANAGEMENT_GROUP + 17;
        case 90: // panel.accounts.MaxDailyLossLimit      
            return AccountDetailsPanel.KEY_RISKMANAGEMENT_GROUP + 18;
        case 86: // panel.accounts.TrailingDrawdownLevel
            return AccountDetailsPanel.KEY_RISKMANAGEMENT_GROUP + 19;
        case 87: // panel.accounts.TotalMaxPositionsQty
            return AccountDetailsPanel.KEY_RISKMANAGEMENT_GROUP + 20;
        //case 51: // panel.accounts.MaxDayVolume    
        //    return AccountDetailsPanel.KEY_RISKMANAGEMENT_GROUP + 21;
        case 52: // panel.accounts.MaxOrdersPerDay  
            return AccountDetailsPanel.KEY_RISKMANAGEMENT_GROUP + 25;
        case 53: // panel.accounts.MaxPositions  
            return AccountDetailsPanel.KEY_RISKMANAGEMENT_GROUP + 30;
        case 54: // panel.accounts.MaxPendingOrders  
            return AccountDetailsPanel.KEY_RISKMANAGEMENT_GROUP + 35;
        case 55: // panel.accounts.MaxOrderCapital 
            return AccountDetailsPanel.KEY_RISKMANAGEMENT_GROUP + 40;
        case 56: // panel.accounts.MaximumLot   
            return AccountDetailsPanel.KEY_RISKMANAGEMENT_GROUP + 45;
        case 57: // panel.accounts.MaxPositionQtyPerSymbol
            return AccountDetailsPanel.KEY_RISKMANAGEMENT_GROUP + 50;
        case 62: // panel.accounts.AllowOvernightTrading                                     
            return AccountDetailsPanel.KEY_RISKMANAGEMENT_GROUP + 55;
        case 85: // panel.accounts.MaxPendingOrders                                     
            return AccountDetailsPanel.KEY_RISKMANAGEMENT_GROUP + 60;

        //VolumeLimitFor.. :
        case 97:
            return AccountDetailsPanel.KEY_RISKMANAGEMENT_GROUP + 69;   // EQUITIES
        case 98:
            return AccountDetailsPanel.KEY_RISKMANAGEMENT_GROUP + 74;   // FUTURES
        case 99:
            return AccountDetailsPanel.KEY_RISKMANAGEMENT_GROUP + 77;   // OPTIONS
        case 118:
            return AccountDetailsPanel.KEY_RISKMANAGEMENT_GROUP + 65;   // ALL
        case 119:
            return AccountDetailsPanel.KEY_RISKMANAGEMENT_GROUP + 72;   // FOREX
        case 120:
            return AccountDetailsPanel.KEY_RISKMANAGEMENT_GROUP + 70;   // EQUITIES CFD
        case 121:
            return AccountDetailsPanel.KEY_RISKMANAGEMENT_GROUP + 73;   // FORWARD
        case 122:
            return AccountDetailsPanel.KEY_RISKMANAGEMENT_GROUP + 75;   // CFD FUTURES
        case 123:
            return AccountDetailsPanel.KEY_RISKMANAGEMENT_GROUP + 76;   // INDICIES
        case 124:
            return AccountDetailsPanel.KEY_RISKMANAGEMENT_GROUP + 68;   // CRYPTO
        case 125:
            return AccountDetailsPanel.KEY_RISKMANAGEMENT_GROUP + 79;   // SPREADBET
        case 126:
            return AccountDetailsPanel.KEY_RISKMANAGEMENT_GROUP + 66;   // BONDS
        case 127:
            return AccountDetailsPanel.KEY_RISKMANAGEMENT_GROUP + 71;   // ETF
        case 128:
            return AccountDetailsPanel.KEY_RISKMANAGEMENT_GROUP + 80;   // TBILL
        case 129:
            return AccountDetailsPanel.KEY_RISKMANAGEMENT_GROUP + 78;   // SPOT
        case 130:
            return AccountDetailsPanel.KEY_RISKMANAGEMENT_GROUP + 67;   // CORPORATE

        case 114:
            return AccountDetailsPanel.KEY_RISKMANAGEMENT_GROUP + 85;

        // panel.accountDetails.Groups.7.Plans #43336

        case 75: // panel.accounts.Commissions
            return AccountDetailsPanel.KEY_PLANS_GROUP + 5;
        case 76: // panel.accounts.Swap
            return AccountDetailsPanel.KEY_PLANS_GROUP + 10;
        case 77: // panel.accounts.Spreads
            return AccountDetailsPanel.KEY_PLANS_GROUP + 15;
        case 78: // panel.accounts.Murkups
            return AccountDetailsPanel.KEY_PLANS_GROUP + 20;
        case 79: // panel.accounts.MarketDataCharging
            return AccountDetailsPanel.KEY_PLANS_GROUP + 25;
        case 80: // panel.accounts.MarginAndRisks
            return AccountDetailsPanel.KEY_PLANS_GROUP + 30;
        case 81: // panel.accounts.MarginAndRisks
            return AccountDetailsPanel.KEY_PLANS_GROUP + 35;

        // panel.accountDetails.Groups.6.Info  


        case 27:  // panel.accounts.Owner  
            return AccountDetailsPanel.KEY_INFO_GROUP + 5;
        case 25:  //panel.accounts.UserID  
            return AccountDetailsPanel.KEY_INFO_GROUP + 10;
        case 26: // panel.accounts.UserLogin  
            return AccountDetailsPanel.KEY_INFO_GROUP + 15;
        case 38:  // panel.accounts.UserGroup  
            return AccountDetailsPanel.KEY_INFO_GROUP + 20;
        case 28: // panel.accounts.Email 
            return AccountDetailsPanel.KEY_INFO_GROUP + 25;
        case 1:// panel.accounts.Login   
            return AccountDetailsPanel.KEY_INFO_GROUP + 30;
        case 67: //panel.accounts.AccountType 
            return AccountDetailsPanel.KEY_INFO_GROUP + 35;
        case 7:// panel.accounts.LockedBy 
            return AccountDetailsPanel.KEY_INFO_GROUP + 40;
        case 63: //panel.accounts.AccountMode  
            return AccountDetailsPanel.KEY_INFO_GROUP + 45;
        case 9:// panel.accounts.ClientType
            return AccountDetailsPanel.KEY_INFO_GROUP + 50;
        case 0:// panel.accounts.Status
            return AccountDetailsPanel.KEY_INFO_GROUP + 55;
        case 41: // panel.accounts.TelephoneNumber  
            return AccountDetailsPanel.KEY_INFO_GROUP + 60;
        case 64: // panel.accounts.Asset  
            return AccountDetailsPanel.KEY_INFO_GROUP + 65;
        case 65:// panel.accounts.AssetDescription 
            return AccountDetailsPanel.KEY_INFO_GROUP + 70;
        case 69: //panel.accounts.AssetType  
            return AccountDetailsPanel.KEY_INFO_GROUP + 75;
        case 100:
            return AccountDetailsPanel.KEY_INFO_GROUP + 80;

        case 8:// panel.accounts.Role   
        case 18: // panel.accounts.stopTradingPercent 
        case 22://  panel.accounts.MarginStopTrade   
        case 47:// panel.accounts.IntradayRiskManagement                                                                                                                                                                                                                                                                                                                                                                       
            return AccountDetailsPanel.KEY_INFO_GROUP + 85;

        default:
            return 1000;
    }
}

AccountDetailsPanel.GetSortIndex = function (assetBalanceItem, headerKey)
{
    if (assetBalanceItem === null)
        return -1;

    var index = assetBalanceItem.FindColumnIndex(headerKey);

    if (index != -1)
        return AccountDetailsPanel.GetSortIndexByIndex(index);
    else
    {
        let item;
        if (assetBalanceItem.Account && assetBalanceItem.Account.AccountAdditionalInfo != null && assetBalanceItem.Account.AccountAdditionalInfo[headerKey])
            return assetBalanceItem.Account.AccountAdditionalInfo[headerKey].SortIndex;
        else if (assetBalanceItem.AssetBalance && assetBalanceItem.AssetBalance.AssetAdditionalInfo != null && assetBalanceItem.AssetBalance.AssetAdditionalInfo[headerKey])
            return assetBalanceItem.AssetBalance.AssetAdditionalInfo[headerKey].SortIndex;
        else
            return -1;
    }
}

AccountDetailsPanel.prototype.GetDynamicItems = function (VerticalPanelDataProvider, sett)
{
    return AccountDetailsPanel.GetDynamicItemsStatic(VerticalPanelDataProvider, sett, this.accountDinamicProperties);
}
AccountDetailsPanel.GetDynamicItemsStatic = function (VerticalPanelDataProvider, sett, accountDinamicProperties)
{
    let res = Array();

    let accItems = VerticalPanelDataProvider.Source;
    if (accItems === null)
        return null;

    //
    // One account case
    //
    if (accItems.length === 1)
    {
        let account = accItems[0].Account;
        if (account === null)
            return null;

        for (let accountAdditionalInfoItem in account.AccountAdditionalInfo)
        {
            accountAdditionalInfoItem = account.AccountAdditionalInfo[accountAdditionalInfoItem];
            var item = AccountDetailsPanel.GetAdditinalInfoItem(accountAdditionalInfoItem, accountDinamicProperties);
            if (item !== null)
                res.push(item);
        }
        let accI_0_AB = accItems[0].AssetBalance;
        if (accI_0_AB && accI_0_AB.AssetAdditionalInfo != null)
        {
            for (let assetAdditionalInfoItem in accI_0_AB.AssetAdditionalInfo)
            {
                assetAdditionalInfoItem = accI_0_AB.AssetAdditionalInfo[assetAdditionalInfoItem];
                let item = AccountDetailsPanel.GetAdditinalInfoItem(assetAdditionalInfoItem, accountDinamicProperties);
                if (item != null)
                    res.push(item);
            }
        }
    }
    //
    // All accounts or AccountGroup
    //
    else
        return null;


    res.sort(function (a, b) { return a.SortIndex - b.SortIndex })

    return res;
}

AccountDetailsPanel.GetAdditinalInfoItem = function (accountAdditionalInfoItem, accountDinamicProperties)
{
    //accountAdditionalInfoItem = account.AccountAdditionalInfo[accountAdditionalInfoItem];
    if (accountAdditionalInfoItem.Destination === AccountAdditionalInfoDestinationPanel.AllPanels
        || accountAdditionalInfoItem.Destination === AccountAdditionalInfoDestinationPanel.AccountsDetails)
    {
        let item = new VerticalPanelDataProviderItem();
        item.Group = accountAdditionalInfoItem.GroupInfo;
        item.GroupIndex = accountAdditionalInfoItem.GroupIndex;
        item.SortIndex = accountAdditionalInfoItem.SortIndex; // sort index inside group
        item.LocalizationKey = accountAdditionalInfoItem.NameKey;
        item.Id = accountAdditionalInfoItem.APIKey;
        item.tooltipKey = accountAdditionalInfoItem.ToolTipKey;
        item.visible = accountAdditionalInfoItem.Visible;


        if (accountDinamicProperties)
        {
            //copy properties
            let baseSesionItem = accountDinamicProperties[accountAdditionalInfoItem.APIKey];
            if (!!baseSesionItem)
                item.Apply(baseSesionItem, false);

            accountDinamicProperties[accountAdditionalInfoItem.APIKey] = item;
        }
        return item;
    }
}

AccountDetailsPanel.GetDataValue = function (key, VerticalPanelDataProvider, sett, rowId)
{
    try
    {
        let qt = this.quickTableRactive.quickTable
        let accItems = VerticalPanelDataProvider.Source;//as List<AssetBalanceItem>;
        let useAssetBalanceItem = true;
        let id = parseInt(key);
        if (isNaN(id))
            useAssetBalanceItem = false;

        //
        // One account case
        //
        if (accItems !== null && accItems.length === 1)
        {
            let assetBalanceItem = accItems[0];
            let val = null, row = qt.rows[rowId],
                hideRowThenUpdateQTScrollAndReturnEmptyString = (qt, row) =>
                {   // #114083
                    let needUpdateScrollElementsCount = row.visible
                    row.visible = false
                    if (needUpdateScrollElementsCount)
                        qt.updateScrollElementsCount()
                    return "";
                }

            if (useAssetBalanceItem)
            {
                let VP = assetBalanceItem.getColumnData(id);
                let FormattedValue = VP.FormattedValue;
                let type = assetBalanceItem.GetDataType(id);

                if (row && FormattedValue == "")
                    return hideRowThenUpdateQTScrollAndReturnEmptyString(qt, row)

                if (row && (type === QuickTableComparingType.Double || type === QuickTableComparingType.Int))
                {
                    let v = QuickTableRow.ColorBySign(row.cells[1], row.table.columns[1], VP.Value);
                    row.cells[1].ForeColor = v.newForeColor;
                }

                return FormattedValue;
            }
            else if (assetBalanceItem.Account !== null && assetBalanceItem.Account.AccountAdditionalInfo !== null)
            {
                let item = assetBalanceItem.Account.AccountAdditionalInfo[key];
                if (!!item)
                    return item.GetFormatValue(assetBalanceItem.AssetBalance);

                if (assetBalanceItem.AssetBalance !== null && assetBalanceItem.AssetBalance.AssetAdditionalInfo !== null)
                {
                    item = assetBalanceItem.AssetBalance.AssetAdditionalInfo[key];
                    if (!!item)
                    {
                        if (row && row.item && !row.visible && !row.cells[1].value)
                        {
                            //Not collapse empty fields
                            let gr_row = qt.groupStorageDict[row.item.GroupStr]
                            if (!gr_row.groupHeaderRow.collapsed)
                                row.visible = true;
                        }

                        let type = assetBalanceItem.GetDataType(rowId)
                        if (row && (type === QuickTableComparingType.Double || type === QuickTableComparingType.Int))
                        {
                            let v = QuickTableRow.ColorBySign(row.cells[1], row.table.columns[1], item.Value);
                            row.cells[1].ForeColor = v.newForeColor;    // #113408
                        }

                        return item.GetFormatValue(assetBalanceItem.AssetBalance);
                    }
                }

                if (row)
                    return hideRowThenUpdateQTScrollAndReturnEmptyString(qt, row)
            }
        }
        //
        // All accounts or AccountGroup
        //
        // else if (accItems !== null && accItems.length > 0)
        // {
        //     if (id === 15 || id === 14 || id === 60)
        //     {
        //         let currentBalance = 0;
        //         let maringUsedForPos = 0;
        //         let maringUsedForPosBalance = 0;
        //         //расчет общего баланса и маржи
        //         for (let ai in accItems)
        //         {
        //             ai = accItems[ai];
        //             if (ai !== null && ai.Account !== null)
        //             {
        //                 currentBalance += ai.Account.CurrentBalanceCrossPrice;
        //                 maringUsedForPos += ai.AssetBalance.MaintMarginForPositionCrossPrice;
        //                 maringUsedForPosBalance += (ai.Account.BalancePlusAllRisksCrossPrice - ai.AssetBalance.BlockedForOrdersCrossPrice);
        //             }
        //         }


        //         // Calculate total
        //         //Current margin - общий
        //         if (id === 14)
        //         {
        //             let marginLevel = 0.0;
        //             for (let j = 0; j < accItems.length; j++)
        //                 marginLevel += accItems[j].getValue(42, null);
        //             return Utils.MoneyFormat.FormatDouble((marginLevel / currentBalance) * 100) + " %";
        //         }

        //         //Maintenance margin - общий
        //         if (id === 15)
        //         {
        //             let res = 0.0;
        //             if (maringUsedForPosBalance !== 0)
        //                 res = Math.Min((maringUsedForPos * 100 / maringUsedForPosBalance), 100);

        //             return Utils.MoneyFormat.FormatDouble(res) + " %";
        //         }

        //         if (id === 60)
        //         {
        //             let fundsused = 0.0;
        //             for (let j = 0; j < accItems.length; j++)
        //                 fundsused += accItems[j].getValue(59, null);
        //             return Utils.MoneyFormat.FormatDouble((fundsused / currentBalance) * 100) + " %";
        //         }
        //     }

        //     let dataType = accItems.length > 0 ? accItems[0].GetDataType(id) : QuickTableComparingType.String;
        //     let total = 0.0;
        //     switch (dataType)
        //     {
        //         case QuickTableComparingType.Double:
        //             {
        //                 for (let j = 0; j < accItems.length; j++)
        //                     total += accItems[j].getValue(id, null);
        //                 break;
        //             }
        //         case QuickTableComparingType.Int:
        //             {
        //                 for (let j = 0; j < accItems.length; j++)
        //                     total += accItems[j].getValue(id, null);
        //                 break;
        //             }
        //         case QuickTableComparingType.Long:
        //             {
        //                 for (let j = 0; j < accItems.length; j++)
        //                     total += accItems[j].getValue(id, null);
        //                 break;
        //             }
        //     }

        //     return Utils.MoneyFormat.FormatDouble(total) + " " + (accItems.length > 0 ? accItems[0].FormatTotal(null, 0, id) : ""); //total[i].toString("N2");  
        // }
    }
    catch (ex)
    {
        ErrorInformationStorage.GetException(ex);
        //Utils.log(ex);
    }
    return "";
}

AccountDetailsPanel.GetItemsID = function (VerticalPanelDataProvider, sett)
{
    let accItems = VerticalPanelDataProvider.Source;//as List<AssetBalanceItem>;

    if (accItems !== null && accItems.length === 1)
    {
        if (accItems[0].Account === null)
            return null;

        let acc = accItems[0].Account;
        // groups
        let idsColumn = new Array();
        let activeItemsIndexes = AccountDetailsPanel.GetActiveItemsIndexes(accItems[0]);
        for (let i = 0; i < activeItemsIndexes.length; i++)
            idsColumn.push(activeItemsIndexes[i].toString());

        let account = accItems[0].Account;
        if (account !== null && account.AccountAdditionalInfo !== null)
            for (let accountAdditionalInfoItem in account.AccountAdditionalInfo)
            {
                accountAdditionalInfoItem = account.AccountAdditionalInfo[accountAdditionalInfoItem];
                if (accountAdditionalInfoItem.Destination === AccountAdditionalInfoDestinationPanel.AllPanels
                    || accountAdditionalInfoItem.Destination === AccountAdditionalInfoDestinationPanel.AccountsDetails)
                    idsColumn.push(accountAdditionalInfoItem.APIKey);
            }

        let asset = accItems[0].AssetBalance;
        if (asset !== null)
            if (asset.AssetAdditionalInfo !== null)
                for (let AssetAdditionalInfo in asset.AssetAdditionalInfo)
                {
                    AssetAdditionalInfo = asset.AssetAdditionalInfo[AssetAdditionalInfo];
                    if (AssetAdditionalInfo.Destination === AccountAdditionalInfoDestinationPanel.AllPanels
                        || AssetAdditionalInfo.Destination === AccountAdditionalInfoDestinationPanel.AccountsDetails)
                        idsColumn.push(AssetAdditionalInfo.APIKey);
                }
        return idsColumn;
    }
    else
    {
        let assetBalanceItem = new AssetBalanceItem(null, sett);
        // groups
        let idsForAll = new Array();
        for (let i = 0; i < assetBalanceItem.ColumnCount(null); i++)
        {
            //remove for "All" -> Leverage, Margin Warning Level, Stop Trading Level, Stop Out Level
            if (i === 16 || i === 17 || i === 18 || i === 19 || i === 37 || i === 39 || i === 40 || (i >= 47 && i <= 57) || i === 63 || i === 67 || i === 69 || i === 12 || i === 13 || i === 44)
                continue;

            let dataType = assetBalanceItem.GetDataType(i);
            if (dataType === QuickTableComparingType.DateTime || dataType === QuickTableComparingType.String)
                continue;
            idsForAll.push(i.toString());
        }
        assetBalanceItem.Dispose();
        assetBalanceItem = null;
        return idsForAll;
    }
}

/// <summary>
/// на смену Source отписка и пр.
/// </summary>
AccountDetailsPanel.DisposeSource = function (VerticalPanelDataProvider)
{
    // очищаем список айтемов
    let processedAccounts = VerticalPanelDataProvider.Source;//as List<AssetBalanceItem>;
    if (processedAccounts !== null)
    {
        for (let i = 0; i < processedAccounts.length; i++)
            processedAccounts[i].Dispose();
        //processedAccounts.Clear();
        processedAccounts = new Array();
    }
}

AccountDetailsPanel.GetActiveItemsIndexes = function (assetItem, hideInfoBlock)
{
    if (hideInfoBlock === undefined)
        hideInfoBlock = false;
    let result = new Array();

    if (assetItem !== null)
    {
        for (let i = 0; i < assetItem.ColumnCount(null); i++)
        {
            if (assetItem.GetColumnParams(i).Hidden)
                continue;

            if (i >= 48 && i <= 57 && i !== 55 && i !== 50)
            {
                let val = assetItem.getColumnValue(i)
                if (val === 0 || val === -1)
                    continue
            }
            if ((i === 50 || i === 55 || i === 86 || i === 87 || i === 88) && assetItem.getColumnValue(i) === -1)
                continue;

            if (i === 68 && assetItem.Account.AccountType !== AccountType.MultiAsset)// #33209, #33495
                continue;

            if (i === 85 && assetItem.getColumnValue(i) === -1)
                continue;

            if (hideInfoBlock && (i === 1 || i >= 25 && i <= 28 || i === 41 || i >= 63 && i <= 65 || i === 67 || i === 69)) // info
                continue;

            if (i === 89 && assetItem.Account.MaxDailyLossLimit === -1)
                continue;

            if (i === 90 && assetItem.Account.MaxWeeklyLossLimit === -1)
                continue;

            if ((i === 94 || i === 95) && assetItem.Account.MaxUrealizedLossLimit === -1)
                continue;

            if ((i === 97 || i === 98 || i === 99) && assetItem.getColumnValue(i) === 0) // VolumeLimit
                continue;

            if (i === 100 && !assetItem.getColumnValue(i))
                continue

            if (i === 101 && assetItem.getColumnValue(i) === -1)
                continue;
            //#88247
            if (i === 104 && (assetItem.Account.WaivedMargin === null || assetItem.Account.AccountType === AccountType.MultiAsset))
                continue;

            if (i === 108 && assetItem.Account.AccountType === AccountType.MultiAsset)
                continue;

            // if (i === 109 && assetItem.getColumnValue(i) < 0)   // commented out because of #108607 <- #108921
            //     continue;

            if (i === 114 && assetItem.Account.MaxRelativeDrawDownLevel !== null || assetItem.Account.MaxRelativeDrawDownLevel === -1)
                continue;

            if (i === 116 && assetItem.Account.LossLimitPerTrade === null)
                continue;

            result.push(i);
        }
    }

    return result;
}

AccountDetailsPanel.SetSource = function (VerticalPanelDataProvider, sett, multiCache, selectedAccs, currentGroupAcc)
{
    currentGroupAcc = null;

    //#region Process selected account

    let processedAccounts = new Array();

    if (selectedAccs)
    {
        // режим всех пользователей
        if (selectedAccs === 'All') //hsa: to remove? I think it is not possible to show few accounts in AccountDetailsPanel
        {
            // получаем все аккаунты
            let accountsList = multiCache.getAccounts();

            // заполняем список
            for (let i = 0; i < accountsList.length; i++)
            {
                let acc = accountsList[i];
                if (acc !== null)
                {
                    if (acc !== null && acc.assetBalanceDefault !== null)
                    {
                        let accItem = new AssetBalanceItem(acc, sett, acc.assetBalanceDefault, acc.assetBalanceDefault.Asset);
                        if (accItem !== null && accItem.Account !== null)
                            processedAccounts.push(accItem);
                    }
                }
            }
        }
        // few, not all
        else if (selectedAccs.indexOf(",") !== -1)
        {
            let accs = selectedAccs.split(',');
            // заполняем список
            for (var str in accs)
            {
                let acc = multiCache.GetAccountById(str);
                if (acc !== null)
                {
                    if (acc.assetBalanceDefault !== null)
                    {
                        let accItem = new AssetBalanceItem(acc, sett, acc.assetBalanceDefault, acc.assetBalanceDefault.Asset);
                        if (accItem !== null && accItem.Account !== null)
                            processedAccounts.push(accItem);
                    }
                }
            }

        }
        // режим одного пользователя
        else
        {
            //проверка на аккаунт-группу
            //TODO
            let groupAcc = null;//multiCache.AccountsGroupManager.GetGroupByName(selectedAccs);
            if (groupAcc !== null)
            {
                currentGroupAcc = groupAcc;
                for (let item in groupAcc.Accounts)
                {
                    if (item.Acc !== null)
                    {
                        if (item.Acc.assetBalanceDefault !== null)
                        {
                            let accItem = new AssetBalanceItem(item.Acc, sett, item.Acc.assetBalanceDefault, item.Acc.assetBalanceDefault.Asset);
                            processedAccounts.push(accItem);
                        }
                    }
                }
            }
            else
            {
                let acc = multiCache.GetAccountById(selectedAccs);

                //TODO
                if (acc !== null /*&& acc.assetBalanceDefault !== null*/)
                {
                    let accItem = new AssetBalanceItem(acc, sett, acc.assetBalanceDefault, null /*acc.assetBalanceDefault.Asset*/);

                    if (accItem !== null && accItem.Account !== null)
                        processedAccounts.push(accItem);
                }
            }
        }
    }
    else
    {
        let accountsList = multiCache.getAccounts();
        if (accountsList !== null && accountsList.length > 0 && accountsList[0] !== null /*&& accountsList[0] is Account*/)
        {
            let acc = accountsList[0];

            if (acc !== null && acc.assetBalanceDefault !== null)
            {
                let accItem = new AssetBalanceItem(acc, sett, acc.assetBalanceDefault, acc.assetBalanceDefault.Asset);
                if (accItem !== null && accItem.Account !== null)
                    processedAccounts.push(accItem);
            }
        }
    }

    //
    VerticalPanelDataProvider.Source = processedAccounts;

    //#endregion
}

AccountDetailsPanel.prototype.SetColumnsColouringMode = function (table)
{
    let up = ThemeManager.CurrentTheme.TableValueUpForeColor;
    let down = ThemeManager.CurrentTheme.TableValueDownForeColor;
    let column = table.columns[1];
    column.ValueUpForeColor = up;
    column.ValueDownForeColor = down;
}